import { useEffect, useState } from 'react';

import { useWaApi } from './api/waApi';

/**
 * What the signed-in user may do with user profiles, as decided by the service from the token's
 * user_role claim. SYSTEM_ADMIN and ADMIN can manage; USER is read only.
 *
 * canViewPii is the same kind of answer for personal data: SYSTEM_ADMIN and ADMIN get the eye icon that unmasks a
 * value and the attachment download; USER only ever sees the masked form. canViewPiiAuditLog (SYSTEM_ADMIN only) is
 * the side panel's "Compliance Audit Log" entry.
 *
 * canManageClientSetup (SYSTEM_ADMIN only) unlocks every write control on the Client Details, Client Portfolio Details
 * and Meta App Details screens - create, edit, enable/disable, refresh cache, sync auth. Everyone else sees them
 * read-only.
 *
 * canManageWhatsAppSetup (SYSTEM_ADMIN and ADMIN) does the same for the WABA Details, Sender Details, Template
 * Categories, System Variables and Templates screens, including their Sync buttons and OTP / PIN controls.
 *
 * canCreateWabaDetail (SYSTEM_ADMIN only) is the one exception on the WABA Details screen: an ADMIN manages the WABAs
 * they hold but only a SYSTEM_ADMIN is offered "+ Create New".
 *
 * Starts as false so management controls stay hidden until the answer arrives, rather than
 * flashing on screen and then disappearing. The service enforces the same rule, so hiding the
 * controls is a convenience, not the access check itself.
 */
export function useUserProfileAccess() {
  const waApi = useWaApi();
  const [access, setAccess] = useState({
    userRole: null,
    canManage: false,
    canViewPii: false,
    canViewPiiAuditLog: false,
    canManageClientSetup: false,
    canManageWhatsAppSetup: false,
    canCreateWabaDetail: false,
    accountActive: false,
    assignableRoles: [],
    assignableWabaIds: [],
    loaded: false,
  });

  useEffect(() => {
    let cancelled = false;
    waApi
      .get(process.env.REACT_APP_GET_MY_WA_USER_ACCESS_ENDPOINT)
      .then((response) => {
        if (cancelled) return;
        const object = response.data?.object ?? {};
        setAccess({
          userRole: object.userRole ?? null,
          canManage: object.canManageUserProfiles === true,
          canViewPii: object.canViewPii === true,
          canViewPiiAuditLog: object.canViewPiiAuditLog === true,
          canManageClientSetup: object.canManageClientSetup === true,
          canManageWhatsAppSetup: object.canManageWhatsAppSetup === true,
          canCreateWabaDetail: object.canCreateWabaDetail === true,
          accountActive: object.accountActive === true,
          // Which roles this user may hand out - SYSTEM_ADMIN grants ADMIN/USER, ADMIN grants USER.
          assignableRoles: Array.isArray(object.assignableRoles) ? object.assignableRoles : [],
          // WABAs on the signed-in user's own profile - the only ones they may hand out.
          assignableWabaIds: Array.isArray(object.assignableWabaIds) ? object.assignableWabaIds : [],
          loaded: true,
        });
      })
      .catch((err) => {
        console.error('Unable to resolve profile permissions:', err);
        if (!cancelled) {
          setAccess({
            userRole: null,
            canManage: false,
            canViewPii: false,
            canViewPiiAuditLog: false,
            canManageClientSetup: false,
            canManageWhatsAppSetup: false,
            canCreateWabaDetail: false,
            accountActive: false,
            assignableRoles: [],
            assignableWabaIds: [],
            loaded: true,
          });
        }
      });
    return () => { cancelled = true; };
  }, [waApi]);

  return access;
}
